// WebSocket connection states
export type WebSocketConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

// Client → Server: subscription target (used in subscribe/unsubscribe)
export interface SubscriptionTarget {
  type: string;
  scope_id?: string;
}

// Client → Server: speed test target
export interface SpeedTestTarget {
  id: string;
  room_no: string;
}

// Client → Server: message envelope
export interface WSClientMessage {
  type: 'subscribe' | 'unsubscribe' | 'speed_test';
  target?: SubscriptionTarget | SpeedTestTarget;
}

// Server → Client: the machine-readable half of an error frame (middleware
// 0.7.18, ticket #91 §4.1). `target` is still the legacy human-readable string
// mirror of `message`; branch on `error.code`, never on `target`.
export interface WSErrorDetail {
  code: string;
  message: string;
}

// Server → Client: message envelope
export interface WSServerMessage {
  type: string;
  target: unknown;
  error?: WSErrorDetail;
  user?: string;
  timestamp?: number;
}

// Error codes that are EXPECTED answers rather than failures: every subscribe
// is checked and the default is to refuse, so a page that asks for a scope it
// may not have gets one of these. They are logged and never toasted (§4.1) —
// an MW light user would otherwise see a toast per page load.
export const QUIET_WS_ERROR_CODES = [
  'subscribe_forbidden',
  'unknown_subscription_type',
  'invalid_target',
] as const;

export function isQuietWsError(code?: string): boolean {
  return !!code && (QUIET_WS_ERROR_CODES as readonly string[]).includes(code);
}

// §4.3 — the server closes the socket (1008, "too many subscribe requests") on
// the 21st subscribe frame inside one second. Pages that follow many scopes at
// once have to stagger them, so the client cap sits safely under the server's.
export const WS_SUBSCRIBE_MAX_PER_SECOND = 20;
export const WS_SUBSCRIBE_BURST = 10;
export const WS_SUBSCRIBE_BURST_GAP_MS = 1100;

// Server → Client: change notification target (standard)
export interface ChangeEventTarget {
  id: string;
  secondary_id?: string;
}

// Server → Client: room_info notification target. `id` is the hotel ID and
// `secondary_id` is the device-facing room number (room_no).
export interface RoomInfoEventTarget {
  id: string;
  secondary_id?: string;
}

// Server → Client: firmware notification target
export interface FirmwareEventTarget {
  platform: string;
  model: string;
}

// Pending change notification stored in Redux for user confirmation
export interface PendingNotification {
  type: string;
  targetId: string;
  secondaryId?: string;
  user: string;
  timestamp: number;
  category: 'project' | 'global';
}

// Speed test tracking state
export interface SpeedTestState {
  status: 'idle' | 'pending' | 'ack' | 'error';
  hotelId?: string;
  roomNo?: string;
  error?: string;
  timestamp?: number;
}

// WebSocket service callback signatures
export interface WSCallbacks {
  onMessage: (msg: WSServerMessage) => void;
  onStatusChange: (status: WebSocketConnectionStatus) => void;
}

export const ROOM_INFO_TYPE = 'room_info' as const;

// Hotel-scoped subscription types (require scope_id = hotelID)
export const HOTEL_SCOPED_TYPES = [
  'project',
  'project_settings',
  'room',
  'local_channel_list',
] as const;

// Global subscription types (scope_id = entityID or empty)
export const GLOBAL_TYPES = [
  'global_channel_list',
  'channel',
  'language',
  'tag',
  'app',
  'firmware',
  'user',
  'track_format',
  // master.json edits (epg_url, Skyway credentials, …) broadcast as
  // {"type":"global","target":{"id":"master"}}
  'global',
] as const;

// target.id carried by a `global` event when /master.json changed
export const GLOBAL_MASTER_ID = 'master';

// All change notification types
export const CHANGE_NOTIFICATION_TYPES = [
  ...HOTEL_SCOPED_TYPES,
  ...GLOBAL_TYPES,
] as const;

export type ChangeNotificationType = typeof CHANGE_NOTIFICATION_TYPES[number];
